<!-- 
	This is the categories page, it uses the dashboard layout in: 
	"./layouts/Dashboard.vue" .
 -->

<template>
	<div class="categories">

		<!-- Header -->
		<a-row :gutter="24" type="flex" class="mb-24">
			<a-col :span="24">
				<a-card :bordered="false" class="header-solid h-full">
					<template #title>
						<h5 class="font-semibold">Categories</h5>
						<p>Manage income and expense categories</p>
					</template>
					<template #extra v-if="canEdit">
						<a-button type="primary" @click="showCreateModal = true">
							<a-icon type="plus" />
							Add Category
						</a-button>
					</template>
				</a-card>
			</a-col>
		</a-row>

		<!-- Categories Lists -->
		<a-row :gutter="24" type="flex">
			<a-col :span="24" :md="12" class="mb-24">
				<a-card title="Income Categories" :loading="loadingIncome" :bordered="false">
					<a-list :data-source="incomeCategories" :locale="{ emptyText: 'No income categories' }">
						<a-list-item slot="renderItem" slot-scope="item">
							<a-list-item-meta :title="item.name" />
						</a-list-item>
					</a-list>
				</a-card>
			</a-col>
			<a-col :span="24" :md="12" class="mb-24">
				<a-card title="Expense Categories" :loading="loadingExpense" :bordered="false">
					<a-list :data-source="expenseCategories" :locale="{ emptyText: 'No expense categories' }">
						<a-list-item slot="renderItem" slot-scope="item">
							<a-list-item-meta :title="item.name" />
						</a-list-item>
					</a-list>
				</a-card>
			</a-col>
		</a-row>

		<!-- Create Category Modal -->
		<a-modal
			title="Create Category"
			:visible="showCreateModal"
			@ok="createCategory"
			@cancel="handleCancelCreate"
			:confirmLoading="creatingCategory"
		>
			<a-form :form="createCategoryForm" layout="vertical">
				<a-form-item label="Name">
					<a-input
						v-decorator="[
							'name',
							{ 
								rules: [
									{ required: true, message: 'Please input name!' },
									{ min: 2, message: 'Name must be at least 2 characters!' }
								] 
							}
						]"
						placeholder="Enter category name"
					/>
				</a-form-item>
				<a-form-item label="Type">
					<a-select
						v-decorator="[
							'type',
							{ 
								rules: [{ required: true, message: 'Please select type!' }],
								initialValue: 'EXPENSE'
							}
						]"
						placeholder="Select type"
					>
						<a-select-option value="INCOME">INCOME</a-select-option>
						<a-select-option value="EXPENSE">EXPENSE</a-select-option>
					</a-select>
				</a-form-item>
			</a-form>
		</a-modal>

	</div>
</template>

<script>
	import { authFetch, OrganizationManager } from '../auth';

	export default ({
		data() {
			return {
				incomeCategories: [],
				expenseCategories: [],
				loadingIncome: false,
				loadingExpense: false,
				showCreateModal: false,
				creatingCategory: false,
			}
		},
		beforeCreate() {
			this.createCategoryForm = this.$form.createForm(this, { name: 'createCategory' });
		},
		mounted() {
			this.fetchCategories();
		},
		computed: {
			canEdit() {
				return OrganizationManager.canEdit();
			}
		},
		methods: {
			async fetchCategories() {
				this.loadingIncome = true;
				this.loadingExpense = true;
				try {
					const [incomeRes, expenseRes] = await Promise.all([
						authFetch(OrganizationManager.buildOrgUrl('/categories/type/INCOME')),
						authFetch(OrganizationManager.buildOrgUrl('/categories/type/EXPENSE'))
					]);

					if (incomeRes.ok) {
						this.incomeCategories = await incomeRes.json();
					} else {
						this.$message.error('Failed to load income categories');
					}
					if (expenseRes.ok) {
						this.expenseCategories = await expenseRes.json();
					} else {
						this.$message.error('Failed to load expense categories');
					}
				} catch (error) {
					console.error('Error fetching categories:', error);
					this.$message.error('Error fetching categories');
				} finally {
					this.loadingIncome = false;
					this.loadingExpense = false;
				}
			},

			async createCategory() {
				this.createCategoryForm.validateFields(async (err, values) => {
					if (!err) {
						this.creatingCategory = true;
						try {
							const url = OrganizationManager.buildOrgUrl('/categories');
							const response = await authFetch(url, {
								method: 'POST',
								body: JSON.stringify(values)
							});

							if (response.ok) {
								this.$message.success('Category created successfully');
								this.showCreateModal = false;
								this.createCategoryForm.resetFields();
								this.fetchCategories();
							} else {
								this.$message.error('Failed to create category');
							}
						} catch (error) {
							console.error('Error creating category:', error);
							this.$message.error('Error creating category');
						} finally {
							this.creatingCategory = false;
						}
					}
				});
			},

			handleCancelCreate() {
				this.showCreateModal = false;
				this.createCategoryForm.resetFields();
			},
		},
		name: 'Categories',
	})
</script>

<style lang="scss">
</style>